/**
 * CardSchema - Template validation and schema checking
 * Ensures template configs are valid and complete
 */

export class CardSchema {
  /**
   * Validate a complete template configuration
   * @param {Object} config - Template config object
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  static validate(config) {
    const errors = [];

    // Required top-level fields
    if (!config.game || typeof config.game !== 'string') {
      errors.push('Missing or invalid "game" field');
    }

    if (!config.name || typeof config.name !== 'string') {
      errors.push('Missing or invalid "name" field');
    }

    if (!config.version || typeof config.version !== 'string') {
      errors.push('Missing or invalid "version" field');
    }

    // Validate card dimensions
    if (!config.cardDimensions) {
      errors.push('Missing "cardDimensions" object');
    } else {
      if (typeof config.cardDimensions.width !== 'number' || config.cardDimensions.width <= 0) {
        errors.push('Invalid cardDimensions.width');
      }
      if (typeof config.cardDimensions.height !== 'number' || config.cardDimensions.height <= 0) {
        errors.push('Invalid cardDimensions.height');
      }
    }

    // Validate card types (if present)
    if (config.cardTypes) {
      if (typeof config.cardTypes !== 'object') {
        errors.push('cardTypes must be an object');
      } else {
        Object.entries(config.cardTypes).forEach(([typeName, typeData]) => {
          if (!typeData.template || typeof typeData.template !== 'string') {
            errors.push(`cardType "${typeName}" missing template path`);
          }
        });
      }
    }

    // Validate sides
    if (!config.sides) {
      errors.push('Missing "sides" object');
    } else {
      // Validate front side
      if (!config.sides.front) {
        errors.push('Missing "sides.front" configuration');
      } else {
        const frontErrors = CardSchema._validateSide(config.sides.front, 'front', config.cardTypes);
        errors.push(...frontErrors);
      }

      // Validate back side (optional but if present, validate)
      if (config.sides.back) {
        const backErrors = CardSchema._validateSide(config.sides.back, 'back', config.cardTypes);
        errors.push(...backErrors);
      }
    }

    // Validate properties
    if (!config.properties) {
      errors.push('Missing "properties" object');
    } else {
      const propErrors = CardSchema._validateProperties(config.properties);
      errors.push(...propErrors);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate a single side configuration
   * @private
   */
  static _validateSide(side, sideName, cardTypes) {
    const errors = [];

    // Check if side uses card types or a static template
    if (side.usesCardType !== undefined && typeof side.usesCardType !== 'boolean') {
      errors.push(`sides.${sideName}.usesCardType must be a boolean`);
    }

    // If not using card types, must have a template
    if (!side.usesCardType && !side.template) {
      errors.push(`sides.${sideName} must have either "usesCardType: true" or a "template" path`);
    }

    // Validate layers array
    if (!side.layers) {
      errors.push(`sides.${sideName} missing "layers" array`);
    } else if (!Array.isArray(side.layers)) {
      errors.push(`sides.${sideName}.layers must be an array`);
    } else {
      side.layers.forEach((layer, index) => {
        const layerErrors = CardSchema._validateLayer(layer, `${sideName}.layers[${index}]`);
        errors.push(...layerErrors);
      });
    }

    return errors;
  }

  /**
   * Validate a layer configuration
   * @private
   */
  static _validateLayer(layer, layerPath) {
    const errors = [];

    // Required fields
    if (!layer.type) {
      errors.push(`${layerPath}: missing "type" field`);
    } else if (!['text', 'image', 'icon'].includes(layer.type)) {
      errors.push(`${layerPath}: invalid type "${layer.type}". Must be "text", "image", or "icon"`);
    }

    if (!layer.id) {
      errors.push(`${layerPath}: missing "id" field`);
    }

    if (!layer.position) {
      errors.push(`${layerPath}: missing "position" object`);
    } else {
      if (typeof layer.position.x !== 'number') {
        errors.push(`${layerPath}: position.x must be a number`);
      }
      if (typeof layer.position.y !== 'number') {
        errors.push(`${layerPath}: position.y must be a number`);
      }
    }

    // Type-specific validation
    if (layer.type === 'text') {
      if (!layer.font) {
        errors.push(`${layerPath}: text layer missing "font" field`);
      }
      if (!layer.color) {
        errors.push(`${layerPath}: text layer missing "color" field`);
      }
    }

    if (layer.type === 'image') {
      if (!layer.size) {
        errors.push(`${layerPath}: image layer missing "size" object`);
      } else {
        if (typeof layer.size.width !== 'number') {
          errors.push(`${layerPath}: size.width must be a number`);
        }
        if (typeof layer.size.height !== 'number') {
          errors.push(`${layerPath}: size.height must be a number`);
        }
      }
    }

    return errors;
  }

  /**
   * Validate properties configuration
   * @private
   */
  static _validateProperties(properties) {
    const errors = [];

    Object.entries(properties).forEach(([propName, propConfig]) => {
      if (!propConfig.type) {
        errors.push(`Property "${propName}": missing "type" field`);
      } else {
        const validTypes = ['string', 'number', 'boolean', 'enum'];
        if (!validTypes.includes(propConfig.type)) {
          errors.push(`Property "${propName}": invalid type "${propConfig.type}"`);
        }

        // Type-specific validation
        if (propConfig.type === 'number') {
          if (propConfig.min !== undefined && typeof propConfig.min !== 'number') {
            errors.push(`Property "${propName}": min must be a number`);
          }
          if (propConfig.max !== undefined && typeof propConfig.max !== 'number') {
            errors.push(`Property "${propName}": max must be a number`);
          }
        }

        if (propConfig.type === 'string') {
          if (propConfig.maxLength !== undefined && typeof propConfig.maxLength !== 'number') {
            errors.push(`Property "${propName}": maxLength must be a number`);
          }
        }

        if (propConfig.type === 'enum') {
          if (!propConfig.values || !Array.isArray(propConfig.values)) {
            errors.push(`Property "${propName}": enum type requires "values" array`);
          }
        }
      }

      // Validate default value exists
      if (propConfig.default === undefined) {
        errors.push(`Property "${propName}": missing "default" value`);
      }
    });

    return errors;
  }

  /**
   * Validate a card state against a template's property schema
   * @param {Object} cardState - Card state to validate
   * @param {Object} properties - Template properties schema
   * @returns {{valid: boolean, errors: Array<string>}}
   */
  static validateCardState(cardState, properties) {
    const errors = [];

    Object.entries(properties).forEach(([propName, propConfig]) => {
      const value = cardState[propName];

      // Check if required property is present
      if (value === undefined || value === null) {
        errors.push(`Missing required property: ${propName}`);
        return;
      }

      // Type validation
      switch (propConfig.type) {
        case 'string':
          if (typeof value !== 'string') {
            errors.push(`Property "${propName}" must be a string`);
          } else if (propConfig.maxLength && value.length > propConfig.maxLength) {
            errors.push(`Property "${propName}" exceeds maxLength of ${propConfig.maxLength}`);
          }
          break;

        case 'number':
          if (typeof value !== 'number' || isNaN(value)) {
            errors.push(`Property "${propName}" must be a number`);
          } else {
            if (propConfig.min !== undefined && value < propConfig.min) {
              errors.push(`Property "${propName}" is below minimum value of ${propConfig.min}`);
            }
            if (propConfig.max !== undefined && value > propConfig.max) {
              errors.push(`Property "${propName}" exceeds maximum value of ${propConfig.max}`);
            }
          }
          break;

        case 'boolean':
          if (typeof value !== 'boolean') {
            errors.push(`Property "${propName}" must be a boolean`);
          }
          break;

        case 'enum':
          if (!propConfig.values.includes(value)) {
            errors.push(`Property "${propName}" must be one of: ${propConfig.values.join(', ')}`);
          }
          break;
      }
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get default card state from properties schema
   * @param {Object} properties - Template properties schema
   * @returns {Object} Default card state
   */
  static getDefaultState(properties) {
    const defaultState = {};

    Object.entries(properties).forEach(([propName, propConfig]) => {
      defaultState[propName] = propConfig.default;
    });

    return defaultState;
  }

  /**
   * Sanitize card state values according to schema rules
   * @param {Object} cardState - Card state to sanitize
   * @param {Object} properties - Template properties schema
   * @returns {Object} Sanitized card state
   */
  static sanitizeCardState(cardState, properties) {
    const sanitized = {};

    Object.entries(properties).forEach(([propName, propConfig]) => {
      let value = cardState[propName];

      // Use default if value is missing
      if (value === undefined || value === null) {
        sanitized[propName] = propConfig.default;
        return;
      }

      // Sanitize by type
      switch (propConfig.type) {
        case 'string':
          value = String(value);
          if (propConfig.maxLength && value.length > propConfig.maxLength) {
            value = value.substring(0, propConfig.maxLength);
          }
          break;

        case 'number':
          value = Number(value);
          if (isNaN(value)) {
            value = propConfig.default;
          } else {
            if (propConfig.min !== undefined) {
              value = Math.max(value, propConfig.min);
            }
            if (propConfig.max !== undefined) {
              value = Math.min(value, propConfig.max);
            }
          }
          break;

        case 'boolean':
          value = Boolean(value);
          break;

        case 'enum':
          if (!propConfig.values.includes(value)) {
            value = propConfig.default;
          }
          break;
      }

      sanitized[propName] = value;
    });

    return sanitized;
  }
}

console.log('CardSchema loaded');
